import type {
    FormSchemaProperty,
    EntityTypeFromApi,
} from '../../../../../core/nutshell-fields/types';
import {translateSchemaEntityTypeToGraphQL} from '../../../../helpers';
import type {FormFieldRowForDuplicateHelpers, View} from '../../../../types';

export const standardBlockNames = ['name', 'email', 'phone', 'note', 'address'] as const;

export const getDisabledText = (
    fields: FormFieldRowForDuplicateHelpers[],
    fieldKey: string,
    entityType: EntityTypeFromApi
): string => {
    // @ts-expect-error
    const hasFirstName = Boolean(fields.find((field) => field.fieldName === 'firstName'));
    // @ts-expect-error
    const hasLastName = Boolean(fields.find((field) => field.fieldName === 'lastName'));

    if (fieldKey === 'firstName') {
        if (hasFirstName) {
            return 'This field is already in the form';
        } else {
            return 'Name is already in the form';
        }
    }

    if (fieldKey === 'lastName') {
        if (hasLastName) {
            return 'This field is already in the form';
        } else {
            return 'Name is already in the form';
        }
    }

    if (fieldKey === 'name' && entityType === 'Contacts') {
        if (hasFirstName && hasLastName) {
            return 'First name and last name are already in the form';
        }
        if (hasFirstName) {
            return 'First name is already in the form';
        }
        if (hasLastName) {
            return 'Last name is already in the form';
        } else {
            return 'This field is already in the form';
        }
    }

    return 'This field is already in the form';
};

export const isDisabled = (fields: FormFieldRowForDuplicateHelpers[], item: FormSchemaProperty) => {
    if (item.canAddMultiple) {
        return false;
    }

    // We disable firstName and lastName fields if person name is already on the form
    if (item.name === 'firstName' || item.name === 'lastName') {
        return Boolean(
            fields.find(
                (field) =>
                    // @ts-expect-error
                    (field.fieldName === item.name || field.fieldName === 'name') &&
                    // @ts-expect-error
                    field.entityType &&
                    // @ts-expect-error
                    field.entityType === translateSchemaEntityTypeToGraphQL(item.entityType)
            )
        );
    }

    // We disable Person name if first name or last name are already on the form
    if (item.name === 'name' && item.entityType === 'Contacts') {
        return Boolean(
            fields.find(
                (field) =>
                    // @ts-expect-error
                    (field.fieldName === 'name' ||
                        // @ts-expect-error
                        field.fieldName === 'firstName' ||
                        // @ts-expect-error
                        field.fieldName === 'lastName') &&
                    // @ts-expect-error
                    field.entityType &&
                    // @ts-expect-error
                    field.entityType === translateSchemaEntityTypeToGraphQL(item.entityType)
            )
        );
    }

    // if a field type is not canAddMultiple, we disable adding another field of the same
    // type if one already exists on the form. i.e. You can't have two person name fields
    // because we wouldn't know which person to map the data to
    return Boolean(
        fields.find(
            (field) =>
                // @ts-expect-error
                (field.fieldName === item.name ||
                    (item.attributeId && // not all fields have attributeId
                        // @ts-expect-error
                        field.attributeId &&
                        // @ts-expect-error
                        field.attributeId === item.attributeId)) &&
                // @ts-expect-error
                field.entityType &&
                // @ts-expect-error
                field.entityType === translateSchemaEntityTypeToGraphQL(item.entityType)
        )
    );
};

export type FieldSortItemType = FormSchemaProperty & {
    isDisabled: boolean;
};

export function getFilteredFields(input: {
    schemaFields: any[];
    fields: FormFieldRowForDuplicateHelpers[];
    view: View;
    excludeFirstLastNames?: boolean;
}): any[] {
    const {schemaFields, fields, view, excludeFirstLastNames} = input;
    let filteredFields = [];

    if (view === 'nutshell-fields') {
        filteredFields = schemaFields
            // return one field per available entityType for each schema field by key
            // (ex. show 'Address' for company and person)
            .reduce<Array<any>>(function (newFields, field) {
                const updatedFields = field.entityTypes
                    // @ts-expect-error
                    .map((type) => {
                        const newField = {...field, entityType: type} as const;

                        return {...newField, isDisabled: isDisabled(fields, newField)};
                    })
                    // @ts-expect-error
                    .filter((f) => {
                        if (excludeFirstLastNames) {
                            return f.name !== 'firstName' && f.name !== 'lastName';
                        } else {
                            return true;
                        }
                    });

                return newFields.concat(updatedFields);
            }, []);
    }

    if (view === 'short-text') {
        filteredFields = schemaFields
            .map((field) => {
                return {...field, isDisabled: isDisabled(fields, field)};
            })
            .filter(
                (field) =>
                    field.isCustomField &&
                    (field.type === 'string' || field.type === 'email' || field.type === 'phone')
            );
    }

    if (view === 'long-text') {
        filteredFields = schemaFields
            .map((field) => {
                return {...field, isDisabled: isDisabled(fields, field)};
            })
            .filter((field) => field.isCustomField && field.type === 'text');
    }

    if (view === 'radio' || view === 'dropdown') {
        filteredFields = schemaFields
            .map((field) => {
                return {...field, isDisabled: isDisabled(fields, field)};
            })
            .filter((field) => field.isCustomField && field.type === 'enum');
    }

    if (view === 'checkbox') {
        filteredFields = schemaFields
            .map((field) => {
                return {...field, isDisabled: isDisabled(fields, field)};
            })
            .filter((field) => field.isCustomField && field.type === 'enum-multiple');
    }

    if (view === 'date') {
        filteredFields = schemaFields
            .map((field) => {
                return {...field, isDisabled: isDisabled(fields, field)};
            })
            .filter((field) => field.isCustomField && field.type === 'date');
    }

    return filteredFields;
}
